import type { Model } from '../../state/model/model';
import type { ModelColorMapping } from '../../state/model/modelColorMapping';
import {
    buildVariantMappingRepairs,
    getApplicableVariantMappingRepairs,
    hasVariantMappingRepairCollisions,
    type ChannelMappingRepair,
    type RepairEntry
} from './variantMappingRepair';

type RepairProduct = Pick<Model, 'colors' | 'colorMappings'>;

const channelKey = (id: string) => id.split('/').pop();

const getCurrentEntries = (product: RepairProduct, channel: ChannelMappingRepair): RepairEntry[] =>
    channel.isMain
        ? product.colors.map((color) => ({ key: color.guid, variantGuid: color.guid, name: color.name, code: color.code }))
        : product.colorMappings.flatMap((mapping, index) => {
              if (
                  channelKey(mapping.salesChannelID) !== channelKey(channel.salesChannelID) ||
                  (mapping.productSKU && mapping.productSKU !== channel.productSKU)
              )
                  return [];
              const color = product.colors.find((candidate) =>
                  mapping.productColor?.guid ? candidate.guid === mapping.productColor.guid : candidate.colorID === mapping.colorID
              );
              // Reserve orphaned mappings' combinations too, but never treat them as writable targets.
              return [{ key: String(index), variantGuid: color?.guid ?? '', name: color?.name ?? '', code: mapping.merchantCode }];
          });

const hasSameSource = (preview: ChannelMappingRepair, current: ChannelMappingRepair) =>
    channelKey(preview.salesChannelID) === channelKey(current.salesChannelID) &&
    preview.isMain === current.isMain &&
    preview.productSKU === current.productSKU &&
    JSON.stringify(preview.context) === JSON.stringify(current.context);

/** Validate the complete proposed batch against current state before preparing any writes. */
const validateRepairBatch = (product: RepairProduct, preview: ChannelMappingRepair, current: ChannelMappingRepair) => {
    if (!hasSameSource(preview, current)) return null;
    const requested = preview.repairs.filter((repair) => repair.reason === null);
    const repairs = getApplicableVariantMappingRepairs(requested);
    if (
        repairs.length !== requested.length ||
        new Set(repairs.map((repair) => repair.key)).size !== repairs.length ||
        new Set(repairs.map((repair) => repair.variantGuid)).size !== repairs.length
    )
        return null;
    const entries = getCurrentEntries(product, current);
    if (new Set(entries.map((entry) => entry.key)).size !== entries.length) return null;
    const recomputed = buildVariantMappingRepairs(entries, current.context.options, current.context.isShopify);
    const valid = repairs.every((repair) => {
        const entry = entries.find((candidate) => candidate.key === repair.key);
        const candidate = recomputed.find((result) => result.key === repair.key);
        return (
            entry &&
            entry.variantGuid &&
            entry.variantGuid === repair.variantGuid &&
            entry.code === repair.code &&
            candidate?.after &&
            candidate.reason === null &&
            JSON.stringify(candidate.after) === JSON.stringify(repair.after) &&
            JSON.stringify(candidate.before) === JSON.stringify(repair.before) &&
            JSON.stringify(candidate.steps) === JSON.stringify(repair.steps)
        );
    });
    // Check the actual submitted subset too: omitted repairs leave their original combinations in place.
    return valid && !hasVariantMappingRepairCollisions(entries, repairs) ? repairs : null;
};

/** Prepare immutable updates, or return null to reject the entire batch without changing the product. */
export function buildVariantMappingUpdates(product: RepairProduct, channel: ChannelMappingRepair, currentChannel: ChannelMappingRepair = channel) {
    const repairs = validateRepairBatch(product, channel, currentChannel);
    if (repairs === null) return null;
    const colorUpdates = channel.isMain
        ? repairs.flatMap((repair) => {
              const color = product.colors.find((candidate) => candidate.guid === repair.variantGuid);
              return color ? [{ color, attributes: repair.after }] : [];
          })
        : [];

    const updateSourceColor = (mapping: ModelColorMapping): ModelColorMapping => {
        const sourceColor = mapping.productColor;
        if (!sourceColor) return mapping;
        const update = colorUpdates.find(({ color }) => (sourceColor.guid ? color.guid === sourceColor.guid : color.colorID === sourceColor.colorID));
        // Unsaved mappings resolve the saved color ID using this source code. Keep merchant codes unchanged.
        return update ? { ...mapping, productColor: { ...sourceColor, code: JSON.stringify(update.attributes) } } : mapping;
    };

    const repairsByKey = new Map(repairs.map((repair) => [repair.key, repair]));
    const updateChannelMapping = (mapping: ModelColorMapping, index: number): ModelColorMapping => {
        const repair = repairsByKey.get(String(index));
        return repair ? { ...mapping, merchantCode: JSON.stringify(repair.after) } : mapping;
    };

    return {
        colorUpdates,
        colorMappings: product.colorMappings.map(channel.isMain ? updateSourceColor : updateChannelMapping)
    };
}

/** Prepare all channels before dispatching anything. A stale/invalid channel rejects the entire batch. */
export function buildMultiChannelVariantMappingUpdates(
    product: RepairProduct,
    channels: readonly ChannelMappingRepair[],
    currentChannels: readonly ChannelMappingRepair[],
    allChannels = false
) {
    if (
        channels.length === 0 ||
        new Set(channels.map((channel) => channelKey(channel.salesChannelID))).size !== channels.length ||
        channels.filter((channel) => channel.isMain).length > 1 ||
        (allChannels &&
            currentChannels.some(
                (current) =>
                    getApplicableVariantMappingRepairs(current.repairs).length > 0 &&
                    !channels.some((channel) => channelKey(channel.salesChannelID) === channelKey(current.salesChannelID))
            ))
    )
        return null;

    return channels.reduce<ReturnType<typeof buildVariantMappingUpdates>>(
        (batch, channel) => {
            if (!batch) return null;
            const current = currentChannels.find((candidate) => channelKey(candidate.salesChannelID) === channelKey(channel.salesChannelID));
            if (!current) return null;
            const updates = buildVariantMappingUpdates({ ...product, colorMappings: batch.colorMappings }, channel, current);
            return updates ? { colorUpdates: [...batch.colorUpdates, ...updates.colorUpdates], colorMappings: updates.colorMappings } : null;
        },
        { colorUpdates: [], colorMappings: product.colorMappings }
    );
}
